import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PageSEO } from "@/components/PageSEO";
import { PageHero } from "@/components/PageHero";
import { Calendar, ArrowRight, Tag } from "lucide-react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLOG_HERO = "https://static.prod-images.emergentagent.com/jobs/236e7b77-d75d-43f0-8b66-78eefb5d55fb/images/25fc476dd48b181af4ee504353b92af7a497980c58ea2374a6822b7405187c8c.jpeg";

const CATEGORY_COLORS = {
  news: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  updates: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  guides: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  policy: "bg-purple-500/20 text-purple-400 border-purple-500/30",
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/blog`).then((res) => {
      setPosts(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? posts : posts.filter((p) => p.category === filter);
  const categories = ["all", ...new Set(posts.map((p) => p.category))];

  return (
    <div className="bg-[#0F172A] min-h-screen">
      <PageSEO page="blog" />
      <Navbar />

      <PageHero
        image={BLOG_HERO}
        tag="News & Insights"
        title="News & Updates"
        subtitle="Latest news, industry updates, policy changes, and investment guides for IIE Sigaddi, Kotdwar"
        breadcrumb={{ home: "Home", current: "Blog" }}
      />

      <div className="bg-[#0F172A] py-12" data-testid="blog-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`blog-filter-${cat}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
                  filter === cat
                    ? "bg-orange-500 text-white border-orange-500"
                    : "border-slate-600 text-slate-400 hover:border-orange-500 hover:text-orange-400"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20 text-slate-400">Loading posts...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400 text-lg mb-2">No blog posts yet</p>
              <p className="text-slate-500 text-sm">Check back soon for news and updates about IIE Sigaddi industrial plots.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post, i) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="block bg-[#1E293B] border border-white/10 hover:border-orange-500/40 transition-all duration-300 group"
                    data-testid={`blog-post-${post.slug}`}
                  >
                    {post.image_url && (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2 py-0.5 text-xs font-bold border capitalize ${CATEGORY_COLORS[post.category] || CATEGORY_COLORS.news}`}>
                          <Tag className="inline w-3 h-3 mr-1" />{post.category}
                        </span>
                        <span className="text-slate-500 text-xs flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {post.created_at?.slice(0, 10)}
                        </span>
                      </div>
                      <h3 className="text-white font-bold text-lg mb-2 group-hover:text-orange-400 transition-colors line-clamp-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                        {post.title}
                      </h3>
                      <p className="text-slate-400 text-sm line-clamp-3">
                        {post.excerpt || post.content?.slice(0, 150) + "..."}
                      </p>
                      <div className="mt-4 flex items-center gap-1 text-orange-400 text-sm font-bold uppercase tracking-wider">
                        Read More <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
